import type { CreateBookingData } from "api-client";
import {
  LOOKUP_MASTERS,
  lookupCacheKey,
  lookupItemsToBookingTypeOptions,
  lookupItemsToEquipmentPiecesOptions,
  lookupItemsToExpiresWhenOptions,
  lookupItemsToStorageTypeOptions,
  lookupItemsToSizeSelectStrings,
  lookupItemsToWheeledEquipmentOptions,
} from "api-client";
import {
  CreateBookingCapacityCostDrawer,
  CreateBookingDrawer,
  CreateBookingDropoffStackEquipmentDrawer,
  CreateBookingDropoffWheeledEquipmentDrawer,
  CreateBookingPickupEquipmentDrawer,
  CreateBookingScheduleDrawer,
  useNotificationSnackbar,
  type CreateBookingDropoffStackEquipmentValues,
  type CreateBookingDropoffWheeledEquipmentValues,
  type CreateBookingOption,
  type CreateBookingPickupEquipmentValues,
  type CreateBookingScheduleStepValues,
  type CreateBookingStepOneValues,
  useLookups,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBookingWizardCustomers } from "../../../shared/hooks/useBookingWizardCustomers";
import { lookupService } from "../../../shared/services/lookup.service";
import { useAppDispatch } from "../../../shared/store/hook";
import { createBooking } from "../../../shared/store/bookings";
import {
  buildPickupEquipmentOptions,
  findPickupStaticEquipmentById,
  resolvePickupStaticEquipmentStorageType,
} from "../constants/pickup-static-equipment.constants";
import {
  bookingWizardCustomerOptions,
  bookingWizardLocationStorageTypes,
  bookingWizardLocationOptions,
  buildCapacityCostLabels,
  buildStackEquipmentCapacityContext,
  resolveDefaultDayRate,
  resolveMultiplePieceQuantityDecision,
  buildOperatingHoursDisplay,
  stripBookingWizardLocationLabel,
  findBookingWizardCustomer,
  findBookingWizardLocation,
  initialSealCaptureRequire,
  resolveBookingWizardLocationTimeZone,
  sealCaptureToggleLabel,
  stepOneSealContextChanged,
} from "../utils/booking-wizard-options";
import { buildCreateBookingRequest, resolveIanaTimeZone, scheduleStepToDraftFields } from "../utils/create-booking-payload.mapper";

export interface CreateBookingWizardProps {
  open: boolean;
  onClose: () => void;
  /** Operator scope for GET /api/bookings/customers. */
  operatorId: number;
  /** Customer detail (and future customer app): lock the customer field. */
  lockedCustomer?: CreateBookingOption | null;
  /** After successful create (FR-AT84-008). */
  onBookingCreated?: (data: CreateBookingData) => void;
}

type WizardFace = "one" | "dropoffStackEquipment" | "equipmentSeal" | "schedule" | "capacityCost";

/** AC-AV186-022 — single source for capacity-zero create failure toast (FR-AV186-024). */
const CAPACITY_BOOKING_NOT_CREATED_TITLE = "Booking not created";
const CAPACITY_BOOKING_NOT_CREATED_DESCRIPTION =
  "There is no capacity. Choose a different customer or location.";

const WIZARD_LOOKUP_MASTERS = [
  LOOKUP_MASTERS.bookingType,
  LOOKUP_MASTERS.bookingStorageType,
  LOOKUP_MASTERS.containerSize,
  LOOKUP_MASTERS.trailerSize,
  LOOKUP_MASTERS.equipmentType,
  LOOKUP_MASTERS.enforcementMode,
  LOOKUP_MASTERS.equipmentPieces,
  LOOKUP_MASTERS.expiresWhen,
] as const;

/**
 * Create booking wizard: step one → equipment → schedule → capacity/cost.
 * Pickup multipiece and dropoff stack multipiece: MAB-only schedule step (operating hours, no scheduled date/time).
 */
export function CreateBookingWizard({
  open,
  onClose,
  operatorId,
  lockedCustomer = null,
  onBookingCreated,
}: CreateBookingWizardProps) {
  const dispatch = useAppDispatch();
  const { showNotification, snackbar } = useNotificationSnackbar();
  const { customers, loading: customersLoading, error: customersLoadError } = useBookingWizardCustomers({
    enabled: open,
    operatorId,
  });
  const {
    byKey: lookupByKey,
    loading: lookupsLoading,
    error: lookupsLoadError,
  } = useLookups(lookupService, WIZARD_LOOKUP_MASTERS, { enabled: open });

  const bookingTypeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.bookingType)] ?? [];
    return lookupItemsToBookingTypeOptions(items);
  }, [lookupByKey]);

  const storageTypeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.bookingStorageType)] ?? [];
    return lookupItemsToStorageTypeOptions(items);
  }, [lookupByKey]);

  const containerSizeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.containerSize)] ?? [];
    return lookupItemsToSizeSelectStrings(items);
  }, [lookupByKey]);

  const trailerSizeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.trailerSize)] ?? [];
    return lookupItemsToSizeSelectStrings(items);
  }, [lookupByKey]);

  const droppedEquipmentOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.equipmentType)] ?? [];
    return lookupItemsToWheeledEquipmentOptions(items);
  }, [lookupByKey]);

  const equipmentPiecesOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.equipmentPieces)] ?? [];
    return lookupItemsToEquipmentPiecesOptions(items);
  }, [lookupByKey]);

  const expiresWhenOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.expiresWhen)] ?? [];
    return lookupItemsToExpiresWhenOptions(items);
  }, [lookupByKey]);

  const enforcementModeLookupItems = useMemo(
    () => lookupByKey[lookupCacheKey(LOOKUP_MASTERS.enforcementMode)] ?? [],
    [lookupByKey],
  );

  const equipmentPiecesLookupItems = useMemo(
    () => lookupByKey[lookupCacheKey(LOOKUP_MASTERS.equipmentPieces)] ?? [],
    [lookupByKey],
  );

  const expiresWhenLookupItems = useMemo(
    () => lookupByKey[lookupCacheKey(LOOKUP_MASTERS.expiresWhen)] ?? [],
    [lookupByKey],
  );

  const combinedCatalogError = customersLoadError ?? lookupsLoadError;
  const catalogsLoading = customersLoading || lookupsLoading;

  const [face, setFace] = useState<WizardFace>("one");
  const [stepOneDraft, setStepOneDraft] = useState<CreateBookingStepOneValues | null>(null);
  const [pickupEquipmentDraft, setPickupEquipmentDraft] = useState<CreateBookingPickupEquipmentValues | null>(null);
  const [dropoffStackEquipmentDraft, setDropoffStackEquipmentDraft] =
    useState<CreateBookingDropoffStackEquipmentValues | null>(null);
  const [dropoffWheeledEquipmentDraft, setDropoffWheeledEquipmentDraft] =
    useState<CreateBookingDropoffWheeledEquipmentValues | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState<CreateBookingScheduleStepValues | null>(null);
  const [dayRateDraft, setDayRateDraft] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stepOneCustomerId, setStepOneCustomerId] = useState<string | null>(null);

  const customerOptions = useMemo(
    () => bookingWizardCustomerOptions(customers, { mode: "create" }),
    [customers],
  );

  const scopedLocationOptions = useMemo(() => {
    const customerId = lockedCustomer?.value ?? stepOneCustomerId;
    return bookingWizardLocationOptions(customers, customerId);
  }, [customers, lockedCustomer?.value, stepOneCustomerId]);

  const locationStorageTypes = useMemo(() => {
    const customerId = lockedCustomer?.value ?? stepOneCustomerId;
    return bookingWizardLocationStorageTypes(customers, customerId);
  }, [customers, lockedCustomer?.value, stepOneCustomerId]);

  const locationTimeZone = useMemo(() => {
    const customerId = stepOneDraft?.customerId ?? lockedCustomer?.value;
    const locationId = stepOneDraft?.locationId;
    if (!customerId || !locationId) return null;
    return resolveBookingWizardLocationTimeZone(customers, customerId, locationId);
  }, [customers, lockedCustomer?.value, stepOneDraft?.customerId, stepOneDraft?.locationId]);

  const capacityCostTimeZone = useMemo(() => resolveIanaTimeZone(locationTimeZone), [locationTimeZone]);

  const selectedCustomer = useMemo(() => {
    const customerId = stepOneDraft?.customerId ?? lockedCustomer?.value;
    return findBookingWizardCustomer(customers, customerId);
  }, [customers, lockedCustomer?.value, stepOneDraft?.customerId]);

  const selectedLocation = useMemo(
    () => findBookingWizardLocation(selectedCustomer, stepOneDraft?.locationId),
    [selectedCustomer, stepOneDraft?.locationId],
  );

  const operatingHoursDisplay = useMemo(
    () => buildOperatingHoursDisplay(selectedLocation?.operatingHours),
    [selectedLocation?.operatingHours],
  );

  const pickupEquipmentOptions = useMemo(() => {
    const isPickupMultipiece =
      stepOneDraft?.bookingType === "pickup" && stepOneDraft?.equipmentPieces === "multiple_pieces";
    return buildPickupEquipmentOptions(null, isPickupMultipiece ? "containers_only" : "all");
  }, [stepOneDraft?.bookingType, stepOneDraft?.equipmentPieces]);

  const selectedPickupStaticEquipment = useMemo(
    () => findPickupStaticEquipmentById(pickupEquipmentDraft?.equipmentId),
    [pickupEquipmentDraft?.equipmentId],
  );

  const capacityCostLabels = useMemo(
    () =>
      buildCapacityCostLabels({
        location: selectedLocation,
        storageType:
          stepOneDraft?.bookingType === "pickup"
            ? resolvePickupStaticEquipmentStorageType(selectedPickupStaticEquipment)
            : (stepOneDraft?.storageType ?? null),
        bookingType: stepOneDraft?.bookingType ?? null,
        equipmentPieces: stepOneDraft?.equipmentPieces ?? null,
        scheduledDateTime: scheduleDraft?.scheduledDateTime ?? null,
        locationTimeZone: capacityCostTimeZone,
        enforcementModeLookupItems,
      }),
    [
      capacityCostTimeZone,
      enforcementModeLookupItems,
      scheduleDraft?.scheduledDateTime,
      selectedPickupStaticEquipment,
      selectedLocation,
      stepOneDraft?.bookingType,
      stepOneDraft?.equipmentPieces,
      stepOneDraft?.storageType,
    ],
  );

  const stackEquipmentCapacityContext = useMemo(
    () =>
      buildStackEquipmentCapacityContext({
        location: selectedLocation,
        storageType: stepOneDraft?.storageType ?? null,
        bookingType: stepOneDraft?.bookingType ?? null,
        equipmentPieces: stepOneDraft?.equipmentPieces ?? null,
        scheduledDateTime: null,
        locationTimeZone: capacityCostTimeZone,
        enforcementModeLookupItems,
      }),
    [
      capacityCostTimeZone,
      enforcementModeLookupItems,
      selectedLocation,
      stepOneDraft?.bookingType,
      stepOneDraft?.equipmentPieces,
      stepOneDraft?.storageType,
    ],
  );

  const resolveMultiplePieceQuantityAction = useCallback(
    (quantity: number) =>
      resolveMultiplePieceQuantityDecision(
        quantity,
        stackEquipmentCapacityContext.remainingSpots,
        stackEquipmentCapacityContext.enforcementMode,
        stackEquipmentCapacityContext.graceInclusiveMaxQuantity,
      ),
    [stackEquipmentCapacityContext],
  );

  const isStackMultiplePieces =
    stepOneDraft?.bookingType === "dropoff" &&
    stepOneDraft?.storageType === "stack" &&
    stepOneDraft?.equipmentPieces === "multiple_pieces";
  const isPickupMultiplePieces =
    stepOneDraft?.bookingType === "pickup" && stepOneDraft?.equipmentPieces === "multiple_pieces";
  const isMultipieceMabOnlySchedule = isStackMultiplePieces || isPickupMultiplePieces;
  const isMultipieceScheduleFinalize = isStackMultiplePieces || isPickupMultiplePieces;
  const isDropoffScheduleOptional =
    stepOneDraft?.bookingType === "dropoff" && !isStackMultiplePieces;
  const isPickupScheduleOptional = stepOneDraft?.bookingType === "pickup";
  const isScheduleOptional = isDropoffScheduleOptional || isPickupScheduleOptional;

  const resetWizardState = useCallback(() => {
    setFace("one");
    setStepOneDraft(null);
    setPickupEquipmentDraft(null);
    setDropoffStackEquipmentDraft(null);
    setDropoffWheeledEquipmentDraft(null);
    setScheduleDraft(null);
    setDayRateDraft(null);
    setSubmitting(false);
    setStepOneCustomerId(lockedCustomer?.value ?? null);
  }, [lockedCustomer?.value]);

  useEffect(() => {
    if (open) {
      resetWizardState();
    }
  }, [open, resetWizardState]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetWizardState();
    onClose();
  }, [onClose, resetWizardState, submitting]);

  const handleStepOneNext = useCallback((values: CreateBookingStepOneValues) => {
    if (stepOneDraft && stepOneSealContextChanged(stepOneDraft, values)) {
      setPickupEquipmentDraft(null);
      setDropoffStackEquipmentDraft(null);
      setDropoffWheeledEquipmentDraft(null);
    }
    setStepOneDraft(values);
    setStepOneCustomerId(values.customerId);
    if (values.bookingType === "dropoff" && values.storageType === "stack") {
      setFace("dropoffStackEquipment");
      return;
    }
    setFace("equipmentSeal");
  }, [stepOneDraft]);

  const handleDropoffStackEquipmentBack = useCallback(() => {
    setFace("one");
  }, []);

  const handleDropoffStackEquipmentNext = useCallback(
    (values: CreateBookingDropoffStackEquipmentValues) => {
      setDropoffStackEquipmentDraft(values);
      setFace("schedule");
    },
    [],
  );

  const notifyCapacityBookingNotCreated = useCallback(() => {
    showNotification({
      variant: "error",
      title: CAPACITY_BOOKING_NOT_CREATED_TITLE,
      description: CAPACITY_BOOKING_NOT_CREATED_DESCRIPTION,
    });
  }, [showNotification]);

  const handleStackEquipmentEndBooking = notifyCapacityBookingNotCreated;

  const handleEquipmentSealBack = useCallback(() => {
    setFace("one");
  }, []);

  const handlePickupEquipmentNext = useCallback((values: CreateBookingPickupEquipmentValues) => {
    setPickupEquipmentDraft(values);
    setFace("schedule");
  }, []);

  const handleDropoffWheeledEquipmentNext = useCallback((values: CreateBookingDropoffWheeledEquipmentValues) => {
    setDropoffWheeledEquipmentDraft(values);
    setFace("schedule");
  }, []);

  const handleScheduleBack = useCallback(
    (values: CreateBookingScheduleStepValues) => {
      setScheduleDraft(values);
      if (stepOneDraft?.bookingType === "dropoff" && stepOneDraft.storageType === "stack") {
        setFace("dropoffStackEquipment");
        return;
      }
      setFace("equipmentSeal");
    },
    [stepOneDraft?.bookingType, stepOneDraft?.storageType],
  );

  const pickupEquipmentLabel = useMemo(() => {
    if (!pickupEquipmentDraft) return null;
    const singleId = pickupEquipmentDraft.equipmentId;
    if (singleId) {
      return pickupEquipmentOptions.find((o) => o.value === singleId)?.label ?? singleId;
    }
    const firstMultiId = pickupEquipmentDraft.equipmentIds[0];
    if (firstMultiId) {
      return pickupEquipmentOptions.find((o) => o.value === firstMultiId)?.label ?? firstMultiId;
    }
    return null;
  }, [pickupEquipmentDraft, pickupEquipmentOptions]);

  const multipieceDefaultDayRate = useMemo(
    () =>
      resolveDefaultDayRate(
        selectedLocation,
        stepOneDraft?.storageType ?? null,
        stepOneDraft?.bookingType ?? null,
      ),
    [selectedLocation, stepOneDraft?.bookingType, stepOneDraft?.storageType],
  );

  const notifyCreateBookingBlocked = useCallback(
    (description: string) => {
      showNotification({
        variant: "error",
        title: "Could not create booking",
        description,
      });
    },
    [showNotification],
  );

  const executeCreateBooking = useCallback(
    async (schedule: CreateBookingScheduleStepValues) => {
      if (!stepOneDraft || submitting) {
        if (!submitting) {
          notifyCreateBookingBlocked("Booking details are incomplete. Return to step one and try again.");
        }
        return;
      }
      if (
        !isMultipieceScheduleFinalize &&
        !isScheduleOptional &&
        !schedule.scheduledDateTime &&
        !schedule.scheduledDatePart
      ) {
        notifyCreateBookingBlocked("Scheduled date and time are required.");
        return;
      }

      if (!isMultipieceScheduleFinalize && capacityCostLabels.action === "end") {
        notifyCapacityBookingNotCreated();
        return;
      }

      if (stepOneDraft.bookingType === "dropoff") {
        if (stepOneDraft.storageType === "stack" && !dropoffStackEquipmentDraft) {
          notifyCreateBookingBlocked("Stack equipment details are missing.");
          return;
        }
        if (stepOneDraft.storageType === "wheeled" && !dropoffWheeledEquipmentDraft) {
          notifyCreateBookingBlocked("Wheeled equipment details are missing.");
          return;
        }
      }

      const resolvedDayRate = isMultipieceScheduleFinalize
        ? multipieceDefaultDayRate
        : (dayRateDraft ?? capacityCostLabels.defaultDayRate);

      const body = buildCreateBookingRequest({
        stepOne: stepOneDraft,
        ...scheduleStepToDraftFields(schedule),
        locationTimeZone,
        costPayload: { dayRate: resolvedDayRate },
        equipmentPiecesLookup: equipmentPiecesLookupItems,
        expiresWhenLookup: expiresWhenLookupItems,
        pickupEquipment: pickupEquipmentDraft,
        dropoffStack: dropoffStackEquipmentDraft,
        dropoffWheeled: dropoffWheeledEquipmentDraft,
        pickupEquipmentLabel,
        pickupStaticEquipment: selectedPickupStaticEquipment,
      });

      setSubmitting(true);
      try {
        const response = await dispatch(createBooking(body)).unwrap();
        onBookingCreated?.(response.data);
        resetWizardState();
        onClose();
      } catch (message) {
        showNotification({
          variant: "error",
          title: "Could not create booking",
          description: typeof message === "string" ? message : "Failed to create booking",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      capacityCostLabels.action,
      capacityCostLabels.defaultDayRate,
      dayRateDraft,
      dispatch,
      dropoffStackEquipmentDraft,
      dropoffWheeledEquipmentDraft,
      equipmentPiecesLookupItems,
      expiresWhenLookupItems,
      isMultipieceScheduleFinalize,
      isScheduleOptional,
      locationTimeZone,
      multipieceDefaultDayRate,
      notifyCapacityBookingNotCreated,
      notifyCreateBookingBlocked,
      onBookingCreated,
      onClose,
      pickupEquipmentDraft,
      pickupEquipmentLabel,
      resetWizardState,
      selectedPickupStaticEquipment,
      showNotification,
      stepOneDraft,
      submitting,
    ],
  );

  const handleScheduleNext = useCallback(
    async (values: CreateBookingScheduleStepValues) => {
      if (isMultipieceScheduleFinalize) {
        setScheduleDraft(values);
        await executeCreateBooking(values);
        return;
      }
      if (!isScheduleOptional && !values.scheduledDateTime && !values.scheduledDatePart) return;
      setScheduleDraft(values);
      setDayRateDraft(capacityCostLabels.defaultDayRate);
      setFace("capacityCost");
    },
    [capacityCostLabels.defaultDayRate, executeCreateBooking, isMultipieceScheduleFinalize, isScheduleOptional],
  );

  const handleCapacityCostBack = useCallback(() => {
    setFace("schedule");
  }, []);

  const handleCapacityCostCreateBooking = useCallback(async () => {
    if (!scheduleDraft) return;
    await executeCreateBooking(scheduleDraft);
  }, [executeCreateBooking, scheduleDraft]);

  const handleCustomerIdChange = useCallback((customerId: string | null) => {
    setStepOneCustomerId(customerId);
  }, []);

  const scheduleLocationLabel = useMemo(() => {
    if (!stepOneDraft) return "Location name";
    const loc = scopedLocationOptions.find((o) => o.value === stepOneDraft.locationId);
    if (loc) return stripBookingWizardLocationLabel(loc.label);
    return "Location name";
  }, [stepOneDraft, scopedLocationOptions]);

  const isPickup = stepOneDraft?.bookingType === "pickup";
  const isDropoff = stepOneDraft?.bookingType === "dropoff";
  const isDropoffWheeled = Boolean(isDropoff && stepOneDraft?.storageType === "wheeled");

  const pickupSealToggleLabel = sealCaptureToggleLabel("pickup");
  const dropoffSealToggleLabel = sealCaptureToggleLabel("dropoff");

  const pickupInitialRequireSealCapture = initialSealCaptureRequire(
    pickupEquipmentDraft,
    selectedCustomer,
    "pickup",
  );
  const dropoffStackInitialRequireSealCapture = initialSealCaptureRequire(
    dropoffStackEquipmentDraft,
    selectedCustomer,
    "dropoff",
  );
  const dropoffWheeledInitialRequireSealCapture = initialSealCaptureRequire(
    dropoffWheeledEquipmentDraft,
    selectedCustomer,
    "dropoff",
  );

  return (
    <>
      <CreateBookingDrawer
        open={open && face === "one"}
        onClose={handleClose}
        customerOptions={customerOptions}
        scopedLocationOptions={scopedLocationOptions}
        customersLoading={catalogsLoading}
        customersLoadError={combinedCatalogError}
        lockedCustomer={lockedCustomer}
        initialValues={stepOneDraft}
        bookingTypeOptions={bookingTypeOptions}
        storageTypeOptions={storageTypeOptions}
        equipmentPiecesOptions={equipmentPiecesOptions}
        locationStorageTypes={locationStorageTypes}
        catalogOptionsLoading={lookupsLoading}
        onCustomerIdChange={handleCustomerIdChange}
        onNext={handleStepOneNext}
      />
      <CreateBookingDropoffStackEquipmentDrawer
        open={open && face === "dropoffStackEquipment"}
        onClose={handleClose}
        onBack={handleDropoffStackEquipmentBack}
        onNext={handleDropoffStackEquipmentNext}
        equipmentPieces={stepOneDraft?.equipmentPieces ?? "one_piece"}
        containerSizeOptions={containerSizeOptions}
        expiresWhenOptions={expiresWhenOptions}
        initialIsReefer={dropoffStackEquipmentDraft?.isReefer ?? null}
        initialContainerNumber={dropoffStackEquipmentDraft?.containerNumber ?? ""}
        initialContainerSize={dropoffStackEquipmentDraft?.containerSize ?? ""}
        initialEquipmentOwner={dropoffStackEquipmentDraft?.equipmentOwner ?? ""}
        initialQuantity={dropoffStackEquipmentDraft?.quantity ?? null}
        initialExpiresWhen={dropoffStackEquipmentDraft?.expiresWhen ?? null}
        initialRequireSealCapture={dropoffStackInitialRequireSealCapture}
        stackSpotsFullLabel={stackEquipmentCapacityContext.stackSpotsFullLabel}
        remainingSpotsLabel={stackEquipmentCapacityContext.remainingSpotsLabel}
        remainingSpotsTone={stackEquipmentCapacityContext.remainingSpotsTone}
        resolveMultiplePieceQuantityAction={
          isStackMultiplePieces ? resolveMultiplePieceQuantityAction : undefined
        }
        onEndBooking={isStackMultiplePieces ? handleStackEquipmentEndBooking : undefined}
        sealCaptureToggleLabel={dropoffSealToggleLabel}
      />
      <CreateBookingPickupEquipmentDrawer
        open={open && face === "equipmentSeal" && Boolean(isPickup)}
        onClose={handleClose}
        onBack={handleEquipmentSealBack}
        onNext={handlePickupEquipmentNext}
        equipmentPieces={stepOneDraft?.equipmentPieces ?? "one_piece"}
        equipmentOptions={pickupEquipmentOptions}
        equipmentLoading={false}
        expiresWhenOptions={expiresWhenOptions}
        initialEquipmentId={pickupEquipmentDraft?.equipmentId ?? null}
        initialEquipmentIds={pickupEquipmentDraft?.equipmentIds ?? []}
        initialQuantity={pickupEquipmentDraft?.quantity ?? null}
        initialExpiresWhen={pickupEquipmentDraft?.expiresWhen ?? null}
        initialRequireSealCapture={pickupInitialRequireSealCapture}
        sealCaptureToggleLabel={pickupSealToggleLabel}
      />
      <CreateBookingDropoffWheeledEquipmentDrawer
        open={open && face === "equipmentSeal" && isDropoffWheeled}
        onClose={handleClose}
        onBack={handleEquipmentSealBack}
        onNext={handleDropoffWheeledEquipmentNext}
        containerSizeSelectOptions={containerSizeOptions}
        trailerSizeSelectOptions={trailerSizeOptions}
        droppedEquipmentOptions={droppedEquipmentOptions}
        initialDroppedOffEquipment={dropoffWheeledEquipmentDraft?.droppedOffEquipment ?? null}
        initialChassisId={dropoffWheeledEquipmentDraft?.chassisId ?? ""}
        initialLicensePlate={dropoffWheeledEquipmentDraft?.licensePlate ?? ""}
        initialEquipmentOwner={dropoffWheeledEquipmentDraft?.equipmentOwner ?? ""}
        initialContainerIsReefer={dropoffWheeledEquipmentDraft?.containerIsReefer ?? null}
        initialContainerNumber={dropoffWheeledEquipmentDraft?.containerNumber ?? ""}
        initialContainerSize={dropoffWheeledEquipmentDraft?.containerSize ?? ""}
        initialContainerEquipmentOwner={dropoffWheeledEquipmentDraft?.containerEquipmentOwner ?? ""}
        initialTrailerIsReefer={dropoffWheeledEquipmentDraft?.trailerIsReefer ?? null}
        initialTrailerNumber={dropoffWheeledEquipmentDraft?.trailerNumber ?? ""}
        initialTrailerSize={dropoffWheeledEquipmentDraft?.trailerSize ?? ""}
        initialTrailerLicensePlate={dropoffWheeledEquipmentDraft?.trailerLicensePlate ?? ""}
        initialTrailerEquipmentOwner={dropoffWheeledEquipmentDraft?.trailerEquipmentOwner ?? ""}
        initialRequireSealCapture={dropoffWheeledInitialRequireSealCapture}
        sealCaptureToggleLabel={dropoffSealToggleLabel}
      />
      <CreateBookingScheduleDrawer
        open={open && face === "schedule"}
        onClose={handleClose}
        onBack={handleScheduleBack}
        onNext={handleScheduleNext}
        locationLabel={scheduleLocationLabel}
        operatingHours={operatingHoursDisplay}
        locationTimeZone={locationTimeZone}
        initialScheduledDateTime={scheduleDraft?.scheduledDateTime ?? null}
        initialMustArriveBy={scheduleDraft?.mustArriveBy ?? null}
        initialScheduledDatePart={scheduleDraft?.scheduledDatePart ?? null}
        initialScheduledTimePart={scheduleDraft?.scheduledTimePart ?? null}
        initialMustArriveDatePart={scheduleDraft?.mustArriveDatePart ?? null}
        initialMustArriveTimePart={scheduleDraft?.mustArriveTimePart ?? null}
        showScheduledDateTime={!isMultipieceMabOnlySchedule}
        showOperatingHours
        scheduledDateTimeRequired={!isScheduleOptional}
        mustArriveByRequired={!isScheduleOptional && !isMultipieceMabOnlySchedule}
        trailingLabel={isMultipieceScheduleFinalize ? "Create booking" : undefined}
        trailingShowNextIcon={isMultipieceScheduleFinalize ? false : undefined}
        submitting={isMultipieceScheduleFinalize ? submitting : false}
        submittingLabel="Creating…"
      />
      <CreateBookingCapacityCostDrawer
        open={open && face === "capacityCost" && !isPickupMultiplePieces}
        onClose={handleClose}
        onBack={handleCapacityCostBack}
        onCreateBooking={handleCapacityCostCreateBooking}
        submitting={submitting}
        primaryActionLabel={capacityCostLabels.primaryActionLabel}
        primaryCapacityLabel={capacityCostLabels.primaryCapacityLabel}
        stackSpotsFullLabel={capacityCostLabels.stackSpotsFullLabel}
        remainingSpotsLabel={capacityCostLabels.remainingSpotsLabel}
        remainingSpotsTone={capacityCostLabels.remainingSpotsTone}
        wheeledSpotsFullLabel={capacityCostLabels.wheeledSpotsFullLabel}
        wheeledRemainingSpotsLabel={capacityCostLabels.wheeledRemainingSpotsLabel}
        wheeledRemainingSpotsTone={capacityCostLabels.wheeledRemainingSpotsTone}
        dayRate={dayRateDraft ?? capacityCostLabels.defaultDayRate}
        onDayRateChange={setDayRateDraft}
        showCostEstimate={capacityCostLabels.showCostEstimate}
        warningMessage={capacityCostLabels.warningMessage}
        warningMessageTone={capacityCostLabels.warningMessageTone ?? undefined}
      />
      {snackbar}
    </>
  );
}
